import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, Users, BarChart3, Shield, ArrowRight } from 'lucide-react';
import './ResourceGuide.css';

function GrowthTips() {
  const tips = [
    {
      icon: <Zap size={32} />,
      title: 'Quick Win #1: Add a Loyalty Program',
      description: 'Reward repeat customers. It costs 5x less to keep a customer than to acquire a new one.',
      action: 'Add 5 points per $1 spent. Offer a free item at 100 points.',
      impact: '+25% repeat purchase rate'
    },
    {
      icon: <Users size={32} />,
      title: 'Quick Win #2: Collect Email Addresses',
      description: 'Email is the most valuable marketing channel. 42x ROI on average.',
      action: 'Offer 10% off first purchase for email signup.',
      impact: '+300% email marketing reach'
    },
    {
      icon: <BarChart3 size={32} />,
      title: 'Quick Win #3: Optimize Your Peak Hours',
      description: 'Most businesses leave money on the table during peak times.',
      action: 'Set higher prices or limit quantities during peak hours.',
      impact: '+15% revenue during peak hours'
    },
    {
      icon: <Shield size={32} />,
      title: 'Quick Win #4: Upsell & Cross-Sell',
      description: 'Recommend complementary products. Average order value increases by 20-30%.',
      action: 'Bundle related items or suggest "customers also bought".',
      impact: '+22% average order value'
    }
  ];

  return (
    <div className="guide-container">
      <div className="guide-header-section">
        <Link to="/resources" className="back-link">← Back to Resources</Link>
        <h1>Growth Tips from Top Performers</h1>
        <p className="guide-subtitle">Quick wins you can implement this week to grow revenue</p>
      </div>

      <div className="guide-content">
        <section className="guide-section">
          <h2>5-Minute Tips That Add Up</h2>
          <p>
            These are small changes that successful businesses have used to grow their revenue. 
            They don't require major overhauls—just smart thinking.
          </p>
        </section>

        <section className="guide-section">
          <h2>The Quick Wins</h2>
          <div className="tips-grid">
            {tips.map((tip, index) => (
              <div key={index} className="tip-card">
                <div className="tip-icon">{tip.icon}</div>
                <h3>{tip.title}</h3>
                <p className="tip-description">{tip.description}</p>
                
                <div className="tip-details">
                  <div className="detail">
                    <strong>How to do it:</strong>
                    <p>{tip.action}</p>
                  </div>
                  <div className="detail impact">
                    <strong>Expected impact:</strong>
                    <p>{tip.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="guide-section">
          <h2>The 80/20 Rule of Growth</h2>
          <p>
            80% of your results come from 20% of your efforts. Focus on these high-impact activities:
          </p>
          <div className="high-impact">
            <div className="impact-item">
              <div className="priority">Priority #1</div>
              <h4>Customer Reviews & Ratings</h4>
              <p>3x more customers will visit if you have good reviews. Invest time here.</p>
            </div>
            <div className="impact-item">
              <div className="priority">Priority #2</div>
              <h4>First-Time Customer Experience</h4>
              <p>Make the first purchase so good they become repeat customers. One great experience = $1000s in lifetime value.</p>
            </div>
            <div className="impact-item">
              <div className="priority">Priority #3</div>
              <h4>Email & SMS Captures</h4>
              <p>Every customer email is worth $1-2 in lifetime value. Collect emails aggressively.</p>
            </div>
            <div className="impact-item">
              <div className="priority">Priority #4</div>
              <h4>Google Business Profile</h4>
              <p>People search Google first. A complete profile can double your local visibility.</p>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>The Psychology of Repeat Purchases</h2>
          <p>
            Why do some businesses have customers for life while others are constantly chasing new customers?
          </p>
          <div className="psychology-cards">
            <div className="psych-card">
              <h3>🎯 Consistency</h3>
              <p>Deliver the same great experience every time. Customers don't forgive inconsistency.</p>
            </div>
            <div className="psych-card">
              <h3>💬 Communication</h3>
              <p>Stay in touch. Email a thank you. Send a birthday offer. Make them feel valued.</p>
            </div>
            <div className="psych-card">
              <h3>🎁 Rewards</h3>
              <p>Loyalty programs work. Make regular customers feel special with points and exclusive offers.</p>
            </div>
            <div className="psych-card">
              <h3>🚀 Surprise & Delight</h3>
              <p>Occasionally surprise with a free upgrade or bonus. Creates emotional connection.</p>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Monthly Checklist for Growth</h2>
          <div className="checklist">
            <div className="check-week">
              <h3>Week 1: Acquisition</h3>
              <ul>
                <li>[ ] Update Google Business Profile</li>
                <li>[ ] Ask for 5+ reviews</li>
                <li>[ ] Create email signup offer</li>
              </ul>
            </div>

            <div className="check-week">
              <h3>Week 2: Activation</h3>
              <ul>
                <li>[ ] Send welcome email to new subscribers</li>
                <li>[ ] Feature top reviews on website/socials</li>
                <li>[ ] Launch loyalty program promotion</li>
              </ul>
            </div>

            <div className="check-week">
              <h3>Week 3: Retention</h3>
              <ul>
                <li>[ ] Send email to inactive customers with offer</li>
                <li>[ ] Reward top customers</li>
                <li>[ ] Analyze what's working, what's not</li>
              </ul>
            </div>

            <div className="check-week">
              <h3>Week 4: Monetization</h3>
              <ul>
                <li>[ ] Test upsells/cross-sells</li>
                <li>[ ] Run a promotion for high-margin items</li>
                <li>[ ] Review metrics and plan for next month</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>The Truth About Growth</h2>
          <p>
            Growth isn't about one big idea. It's about small improvements compounding over time. 
            A 10% improvement each month becomes 3x growth in a year.
          </p>
          <p>
            The businesses that win are the ones that stay consistent, measure results, and keep optimizing. 
            You don't need to do everything. You just need to do the right things.
          </p>
        </section>

        <section className="guide-cta">
          <h2>Let's Get Growing</h2>
          <p>Our platform makes it easy to implement all these growth strategies. See how.</p>
          <Link to="/features" className="guide-cta-btn">Explore Growth Tools <ArrowRight size={18} /></Link>
        </section>
      </div>

      <div className="guide-navigation">
        <Link to="/resources/best-practices" className="next-guide">
          Next: Best Practices <ArrowRight size={18} />
        </Link>
      </div>
    </div>
  );
}

export default GrowthTips;
